import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Settings, Users, TrendingUp, ScrollText, Plus, Upload, Check, Pencil, Trash2, Copy, KeyRound, Mail, Eye } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useProjects } from "@/context/ProjectContext";
import { PageHeader, SectionCard, Loading, EmptyState, Pagination } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { formatTanggalWaktu } from "@/lib/format";
import { toast } from "sonner";

const ACTION_LABELS = {
  create: "Buat", update: "Ubah", approve: "Setujui", reject: "Tolak", submit: "Ajukan",
  cancel: "Batalkan", send: "Kirim", reconcile: "Rekonsiliasi", resolve: "Selesaikan",
  raise: "Ajukan Konflik", movein: "Masuk Kamar", extension: "Perpanjangan",
  early_exit: "Keluar Lebih Awal", settlement: "Pelunasan", upload: "Unggah", login: "Masuk",
  delete: "Hapus", direct_rent: "Langsung Sewa", deposit_refund: "Kembalikan Deposit",
};
const OBJECT_LABELS = {
  house: "Rumah", room: "Kamar", room_pricing: "Harga Kamar", room_condition: "Kondisi Kamar", project: "Proyek",
  gh_building: "Gedung Guest House", gh_room: "Kamar Guest House", investor: "Investor",
  investor_disbursement: "Pencairan Investor", booking: "Booking", lease: "Sewa", payment: "Pembayaran",
  resident: "Penghuni", gh_stay: "Menginap Guest House", gh_booking: "Booking Guest House", gh_lease: "Sewa Guest House",
  approval_request: "Pengajuan", user: "Pengguna", admin_digest: "Email Aktivitas Admin",
};

export default function Pengaturan() {
  const { isSuperadmin, canManage } = useAuth();
  return (
    <div className="space-y-5">
      <PageHeader title="Pengaturan" description="Identitas pengelola, konfigurasi bisnis, pengguna, investor, dan audit." />
      <Tabs defaultValue="umum">
        <TabsList className="flex-wrap">
          <TabsTrigger value="umum" data-testid="tab-umum"><Settings className="mr-1.5 h-4 w-4" /> Umum</TabsTrigger>
          {isSuperadmin && <TabsTrigger value="pengguna" data-testid="tab-pengguna"><Users className="mr-1.5 h-4 w-4" /> Pengguna</TabsTrigger>}
          {canManage && <TabsTrigger value="investor" data-testid="tab-investor"><TrendingUp className="mr-1.5 h-4 w-4" /> Investor</TabsTrigger>}
          <TabsTrigger value="audit" data-testid="tab-audit"><ScrollText className="mr-1.5 h-4 w-4" /> Audit</TabsTrigger>
        </TabsList>
        <TabsContent value="umum" className="mt-4"><UmumTab isSuperadmin={isSuperadmin} /></TabsContent>
        {isSuperadmin && <TabsContent value="pengguna" className="mt-4"><PenggunaTab /></TabsContent>}
        {canManage && <TabsContent value="investor" className="mt-4"><InvestorTab isSuperadmin={isSuperadmin} /></TabsContent>}
        <TabsContent value="audit" className="mt-4"><AuditTab isSuperadmin={isSuperadmin} /></TabsContent>
      </Tabs>
    </div>
  );
}

function UmumTab({ isSuperadmin }) {
  const qc = useQueryClient();
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [name, setName] = useState("");
  const saveName = async () => {
    try { await api.patch("/settings", { manager_name: name || settings.manager_name }); toast.success("Nama pengelola disimpan."); qc.invalidateQueries({ queryKey: ["settings"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const onLogo = (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try { await api.post("/settings/logo", { logo_base64: reader.result, mime: file.type }); toast.success("Logo diunggah."); qc.invalidateQueries({ queryKey: ["settings"] }); }
      catch (err) { toast.error(errMsg(err)); }
    };
    reader.readAsDataURL(file);
  };
  if (!settings) return <Loading />;
  return (
    <div className="space-y-5">
      <SectionCard title="Identitas Pengelola">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div>
            <Label>Nama Pengelola</Label>
            <div className="mt-1 flex gap-2">
              <Input defaultValue={settings.manager_name} onChange={(e) => setName(e.target.value)} disabled={!isSuperadmin} data-testid="manager-name-input" />
              {isSuperadmin && <Button onClick={saveName} data-testid="manager-name-save">Simpan</Button>}
            </div>
          </div>
          <div>
            <Label>Logo</Label>
            <div className="mt-1 flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary text-white font-bold">
                {settings.has_logo ? <Check className="h-5 w-5" /> : "S"}
              </div>
              {isSuperadmin ? (
                <label className="inline-flex cursor-pointer items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted" data-testid="logo-upload-label">
                  <Upload className="h-4 w-4" /> {settings.has_logo ? "Ganti Logo" : "Unggah Logo"}
                  <input type="file" accept="image/png,image/jpeg" className="hidden" onChange={onLogo} data-testid="logo-upload-input" />
                </label>
              ) : <span className="text-xs text-muted-foreground">{settings.has_logo ? "Logo tersedia" : "Belum ada logo"}</span>}
            </div>
            <p className="mt-1 text-xs text-muted-foreground">Logo dipakai di sidebar, struk PDF, dan laporan. Jika belum ada, dipakai placeholder nama.</p>
          </div>
        </div>
      </SectionCard>

      {isSuperadmin && <ScheduleCard settings={settings} />}

      <SectionCard title="Warna Identitas">
        <div className="flex flex-wrap gap-4">
          {[["Oranye", settings.colors.orange], ["Gelap", settings.colors.dark], ["Putih", settings.colors.white]].map(([k, v]) => (
            <div key={k} className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg border" style={{ backgroundColor: v }} />
              <div className="text-sm"><div className="font-medium">{k}</div><div className="text-xs text-muted-foreground">{v}</div></div>
            </div>
          ))}
        </div>
      </SectionCard>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <SectionCard title="Konfigurasi Bisnis">
          <dl className="space-y-2 text-sm">
            <Row k="DP tetap" v={`Rp ${settings.config.dp_amount.toLocaleString("id-ID")}`} />
            <Row k="Masa tahan booking" v={`${settings.config.booking_hold_hours} jam`} />
            <Row k="Potongan perawatan" v={`${settings.config.care_percent}%`} />
            <Row k="Paket sewa kos" v={`${settings.config.package_months.join(", ")} bulan`} />
            <Row k="Paket 1 bulan" v="Opsional (aktif bila tarif 1 bln diisi pada kamar)" />
            <Row k="Deposit jaminan (default)" v={`Rp ${(settings.config.deposit_default || 0).toLocaleString("id-ID")}`} />
            <Row k="Zona waktu" v={settings.config.timezone} />
          </dl>
        </SectionCard>
        <SectionCard title="Asumsi Awal (Terdokumentasi)">
          <ul className="list-disc space-y-1.5 pl-5 text-xs text-muted-foreground">
            {settings.assumptions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </SectionCard>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return <div className="flex justify-between border-b border-border pb-1.5"><dt className="text-muted-foreground">{k}</dt><dd className="font-medium">{v}</dd></div>;
}

function ScheduleCard({ settings }) {
  const qc = useQueryClient();
  const sch = settings.summary_schedule || { enabled: true, day_of_month: 1, hour_wib: 8, channel: "email" };
  const [f, setF] = useState({ enabled: sch.enabled, day_of_month: sch.day_of_month, hour_wib: sch.hour_wib });
  const save = async () => {
    try {
      await api.patch("/settings/summary-schedule", { enabled: f.enabled, day_of_month: Number(f.day_of_month), hour_wib: Number(f.hour_wib), channel: sch.channel || "email" });
      toast.success("Jadwal pengingat disimpan.");
      qc.invalidateQueries({ queryKey: ["settings"] });
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <SectionCard title="Jadwal Pengingat Transfer Investor" description="Ringkasan sisa pencairan dikirim otomatis ke email superadmin setiap bulan pada tanggal & jam (WIB) berikut.">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="flex items-center gap-2"><Switch checked={f.enabled} onCheckedChange={(v) => setF({ ...f, enabled: v })} data-testid="schedule-enabled" /><Label className="!mb-0">Aktif</Label></div>
        <div><Label>Tanggal (1–28)</Label><Input type="number" min="1" max="28" value={f.day_of_month} onChange={(e) => setF({ ...f, day_of_month: e.target.value })} data-testid="schedule-day" /></div>
        <div><Label>Jam (WIB, 0–23)</Label><Input type="number" min="0" max="23" value={f.hour_wib} onChange={(e) => setF({ ...f, hour_wib: e.target.value })} data-testid="schedule-hour" /></div>
        <div className="flex items-end"><Button onClick={save} className="w-full" data-testid="schedule-save">Simpan Jadwal</Button></div>
      </div>
      {sch.last_sent_period && <p className="mt-2 text-xs text-muted-foreground">Terakhir terkirim untuk periode: {sch.last_sent_period}.</p>}
    </SectionCard>
  );
}

function PenggunaTab() {
  const qc = useQueryClient();
  const { data: users = [], isLoading } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });
  const { data: projects = [] } = useQuery({ queryKey: ["projects"], queryFn: async () => (await api.get("/projects")).data });
  const [resetUser, setResetUser] = useState(null);
  const toggle = useMutation({
    mutationFn: async ({ id, active }) => (await api.patch(`/users/${id}`, { active })).data,
    onSuccess: () => { toast.success("Status akun diperbarui."); qc.invalidateQueries({ queryKey: ["users"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const pName = (ids) => ids.map((id) => projects.find((p) => p.id === id)?.name).filter(Boolean).join(", ") || "-";
  if (isLoading) return <Loading />;
  return (
    <SectionCard title="Pengguna" description="Superadmin membuat & menonaktifkan akun admin, menugaskan proyek, serta mereset kata sandi."
      actions={<CreateUser projects={projects} onDone={() => qc.invalidateQueries({ queryKey: ["users"] })} />} className="!p-0 overflow-hidden">
      <div className="overflow-x-auto">
      <Table>
        <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Email</TableHead><TableHead>Peran</TableHead><TableHead>Proyek</TableHead><TableHead>Login Terakhir</TableHead><TableHead className="text-center">Aktif</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
        <TableBody>
          {users.map((u) => (
            <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
              <TableCell className="font-medium">{u.name}</TableCell>
              <TableCell className="text-muted-foreground">{u.email}</TableCell>
              <TableCell className="text-xs">{u.role === "superadmin" ? "Superadmin" : "Admin Proyek"}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{u.role === "superadmin" ? "Semua proyek" : pName(u.project_ids)}</TableCell>
              <TableCell className="whitespace-nowrap text-xs text-muted-foreground" data-testid={`user-last-login-${u.id}`}>{u.last_login_at ? formatTanggalWaktu(u.last_login_at) : "Belum pernah"}</TableCell>
              <TableCell className="text-center">
                <Switch checked={u.active} disabled={u.role === "superadmin"} onCheckedChange={(v) => toggle.mutate({ id: u.id, active: v })} data-testid={`user-active-${u.id}`} />
              </TableCell>
              <TableCell className="text-right">
                <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => setResetUser(u)} title="Reset kata sandi" data-testid={`user-reset-${u.id}`}><KeyRound className="h-4 w-4" /></Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      </div>
      {resetUser && <ResetPassword target={resetUser} onClose={() => setResetUser(null)} />}
    </SectionCard>
  );
}

function ResetPassword({ target, onClose }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (password.length < 8) { toast.error("Kata sandi minimal 8 karakter."); return; }
    setLoading(true);
    try { await api.post(`/users/${target.id}/reset-password`, { password }); toast.success(`Kata sandi ${target.name} diperbarui.`); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader><DialogTitle>Reset Kata Sandi</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">Atur kata sandi baru untuk <span className="font-medium text-foreground">{target.name}</span> ({target.email}). Kata sandi langsung berlaku.</p>
          <div><Label>Kata Sandi Baru (min. 8 karakter)</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="reset-password-input" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="reset-password-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CreateUser({ projects, onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ email: "", name: "", password: "", project_ids: [] });
  const toggleProject = (id) => setF((s) => ({ ...s, project_ids: s.project_ids.includes(id) ? s.project_ids.filter((x) => x !== id) : [...s.project_ids, id] }));
  const save = async () => {
    try { await api.post("/users", f); toast.success("Akun admin dibuat."); setOpen(false); setF({ email: "", name: "", password: "", project_ids: [] }); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="add-user-button"><Plus className="mr-1.5 h-4 w-4" /> Tambah Admin</Button></DialogTrigger>
      <DialogContent>
        <DialogHeader><DialogTitle>Tambah Akun Admin</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="user-name" /></div>
          <div><Label>Email</Label><Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="user-email" /></div>
          <div><Label>Kata Sandi (min. 8 karakter)</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="user-password" /></div>
          <div>
            <Label>Proyek yang Ditugaskan</Label>
            <div className="mt-1 space-y-1.5 rounded-lg border border-border p-3">
              {projects.map((p) => (
                <label key={p.id} className="flex items-center gap-2 text-sm">
                  <input type="checkbox" checked={f.project_ids.includes(p.id)} onChange={() => toggleProject(p.id)} data-testid={`user-project-${p.code}`} />
                  {p.name}
                </label>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="user-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvestorTab({ isSuperadmin }) {
  const qc = useQueryClient();
  const { projects = [], projectId, setProjectId } = useProjects();
  const { data: investors = [], isLoading } = useQuery({ queryKey: ["investors"], queryFn: async () => (await api.get("/investors")).data });
  const [editInv, setEditInv] = useState(null);
  const copyRek = async (no) => {
    try { await navigator.clipboard.writeText(no); toast.success("Nomor rekening disalin."); }
    catch { toast.error("Gagal menyalin. Salin manual."); }
  };
  const del = async (i) => {
    if (!window.confirm(`Hapus investor "${i.name}"? Hanya dapat dihapus jika tidak memiliki rumah.`)) return;
    try { await api.delete(`/investors/${i.id}`); toast.success("Investor dihapus."); qc.invalidateQueries({ queryKey: ["investors"] }); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (isLoading) return <Loading />;
  const shown = projectId === "all" ? projects : projects.filter((p) => p.id === projectId);
  const groups = shown.map((p) => ({ id: p.id, name: p.name, items: investors.filter((i) => (i.project_ids || []).includes(p.id)) }));
  const orphans = investors.filter((i) => !(i.project_ids || []).length);
  if (projectId === "all" && orphans.length) groups.push({ id: "none", name: "Tanpa Proyek", items: orphans });
  return (
    <SectionCard title="Investor per Proyek" description={isSuperadmin ? "Investor dikelompokkan per proyek. Investor yang memiliki rumah di beberapa proyek tampil di setiap proyeknya." : "Investor di proyek Anda. Perubahan & penghapusan data investor dilakukan superadmin."}
      actions={<div className="flex flex-wrap items-center gap-2">
        <Select value={projectId} onValueChange={setProjectId}>
          <SelectTrigger className="w-48" data-testid="investor-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Proyek</SelectItem>
            {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <CreateInvestor onDone={() => qc.invalidateQueries({ queryKey: ["investors"] })} />
      </div>} className="!p-0 overflow-hidden">
      {groups.every((g) => g.items.length === 0) ? <div className="p-6"><EmptyState title="Belum ada investor" icon={TrendingUp} /></div> : (
        <Table>
          <TableHeader><TableRow><TableHead>Nama</TableHead><TableHead>Telepon</TableHead><TableHead>Rekening Bank</TableHead><TableHead>Rumah</TableHead><TableHead>Catatan</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
          <TableBody>
            {groups.map((g) => [
              <TableRow key={`g-${g.id}`} className="bg-muted/40 hover:bg-muted/40" data-testid={`investor-group-${g.id}`}>
                <TableCell colSpan={6} className="py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{g.name} · {g.items.length} investor</TableCell>
              </TableRow>,
              ...(g.items.length === 0 ? [<TableRow key={`e-${g.id}`}><TableCell colSpan={6} className="text-xs text-muted-foreground">Belum ada investor di proyek ini.</TableCell></TableRow>] : g.items.map((i) => (
                <InvestorRow key={`${g.id}-${i.id}`} i={i} groupId={g.id} isSuperadmin={isSuperadmin} onCopy={copyRek} onEdit={setEditInv} onDelete={del} />
              ))),
            ])}
          </TableBody>
        </Table>
      )}
      {editInv && <EditInvestor investor={editInv} onClose={() => setEditInv(null)} onDone={() => { setEditInv(null); qc.invalidateQueries({ queryKey: ["investors"] }); }} />}
    </SectionCard>
  );
}

function InvestorRow({ i, groupId, isSuperadmin, onCopy, onEdit, onDelete }) {
  const houses = groupId === "none" ? 0 : (i.house_count_by_project?.[groupId] || 0);
  return (
    <TableRow data-testid={`investor-row-${groupId}-${i.id}`}>
      <TableCell className="font-medium">{i.name}{(i.project_ids || []).length > 1 && <span className="ml-2 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700">{i.project_ids.length} proyek</span>}</TableCell>
      <TableCell className="text-muted-foreground">{i.phone || "-"}</TableCell>
      <TableCell>
        {i.bank_account_no ? (
          <div className="flex items-center gap-2">
            <div className="text-xs leading-tight">
              <div className="font-medium text-foreground">{i.bank_name || "Bank"}</div>
              <div className="font-mono">{i.bank_account_no}</div>
              {i.bank_account_holder && <div className="text-muted-foreground">a.n. {i.bank_account_holder}</div>}
            </div>
            <Button size="sm" variant="ghost" className="h-7 px-2" title="Salin nomor rekening" onClick={() => onCopy(i.bank_account_no)} data-testid={`investor-copy-${groupId}-${i.id}`}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        ) : <span className="text-xs text-muted-foreground">-</span>}
      </TableCell>
      <TableCell className="text-sm" data-testid={`investor-houses-${groupId}-${i.id}`}>{houses} rumah</TableCell>
      <TableCell className="text-xs text-muted-foreground">{i.note || "-"}</TableCell>
      <TableCell className="text-right">
        {isSuperadmin && <div className="flex items-center justify-end gap-1">
          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => onEdit(i)} data-testid={`investor-edit-${groupId}-${i.id}`}><Pencil className="h-4 w-4" /></Button>
          <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive" onClick={() => onDelete(i)} data-testid={`investor-delete-${groupId}-${i.id}`}><Trash2 className="h-4 w-4" /></Button>
        </div>}
      </TableCell>
    </TableRow>
  );
}

function InvestorProjectChecks({ value, onChange, locked = [] }) {
  const { projects = [] } = useProjects();
  const toggle = (id, on) => onChange(on ? [...new Set([...value, id])] : value.filter((v) => v !== id));
  return (
    <div className="rounded-lg border border-border p-3" data-testid="investor-projects">
      <div className="mb-2 text-xs font-medium text-muted-foreground">Proyek Investor (wajib, boleh lebih dari satu)</div>
      <div className="space-y-2">
        {projects.map((p) => (
          <label key={p.id} className="flex items-center gap-2 text-sm">
            <Checkbox checked={value.includes(p.id) || locked.includes(p.id)} disabled={locked.includes(p.id)} onCheckedChange={(v) => toggle(p.id, !!v)} data-testid={`investor-project-${p.id}`} />
            {p.name}{locked.includes(p.id) && <span className="text-xs text-muted-foreground">(punya rumah di proyek ini)</span>}
          </label>
        ))}
      </div>
    </div>
  );
}

function InvestorBankFields({ f, setF, prefix }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-3">
      <div className="mb-2 text-xs font-medium text-muted-foreground">Rekening Bank (untuk pencairan)</div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div><Label className="text-xs">Nama Bank</Label><Input value={f.bank_name} onChange={(e) => setF({ ...f, bank_name: e.target.value })} placeholder="mis. BCA" data-testid={`${prefix}-bank-name`} /></div>
        <div><Label className="text-xs">Nomor Rekening</Label><Input value={f.bank_account_no} onChange={(e) => setF({ ...f, bank_account_no: e.target.value })} inputMode="numeric" data-testid={`${prefix}-bank-no`} /></div>
      </div>
      <div className="mt-2"><Label className="text-xs">Atas Nama</Label><Input value={f.bank_account_holder} onChange={(e) => setF({ ...f, bank_account_holder: e.target.value })} data-testid={`${prefix}-bank-holder`} /></div>
    </div>
  );
}

function CreateInvestor({ onDone }) {
  const { projects = [], projectId } = useProjects();
  const [open, setOpen] = useState(false);
  const defaultPids = () => (projectId !== "all" ? [projectId] : projects.length === 1 ? [projects[0].id] : []);
  const empty = { name: "", phone: "", note: "", bank_name: "", bank_account_no: "", bank_account_holder: "" };
  const [f, setF] = useState(empty);
  const [pids, setPids] = useState([]);
  const onOpen = (o) => { setOpen(o); if (o) setPids(defaultPids()); };
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama investor wajib diisi."); return; }
    if (!pids.length) { toast.error("Pilih minimal satu proyek investor."); return; }
    try { await api.post("/investors", { ...f, project_ids: pids }); toast.success("Investor ditambahkan."); setOpen(false); setF(empty); onDone(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpen}>
      <DialogTrigger asChild><Button size="sm" data-testid="add-investor-button"><Plus className="mr-1.5 h-4 w-4" /> Tambah Investor</Button></DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Tambah Investor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="investor-name" /></div>
          <InvestorProjectChecks value={pids} onChange={setPids} />
          <div><Label>Telepon</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="investor-phone" /></div>
          <InvestorBankFields f={f} setF={setF} prefix="investor" />
          <div><Label>Catatan</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="investor-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="investor-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditInvestor({ investor, onClose, onDone }) {
  const [f, setF] = useState({
    name: investor.name || "", phone: investor.phone || "", note: investor.note || "",
    bank_name: investor.bank_name || "", bank_account_no: investor.bank_account_no || "", bank_account_holder: investor.bank_account_holder || "",
  });
  const [pids, setPids] = useState((investor.owner_project_ids || []).filter((p) => !(investor.house_project_ids || []).includes(p)));
  const locked = investor.house_project_ids || [];
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (!f.name.trim()) { toast.error("Nama investor wajib diisi."); return; }
    if (![...pids, ...locked].length) { toast.error("Pilih minimal satu proyek investor."); return; }
    setLoading(true);
    try { await api.patch(`/investors/${investor.id}`, { ...f, project_ids: pids }); toast.success("Investor diperbarui."); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="investor-edit-dialog">
        <DialogHeader><DialogTitle>Ubah Investor</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nama</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="investor-edit-name" /></div>
          <InvestorProjectChecks value={pids} onChange={setPids} locked={locked} />
          <div><Label>Telepon</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="investor-edit-phone" /></div>
          <InvestorBankFields f={f} setF={setF} prefix="investor-edit" />
          <div><Label>Catatan</Label><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="investor-edit-note" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={loading} data-testid="investor-edit-save">Simpan</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AuditTab({ isSuperadmin }) {
  const { projects = [] } = useProjects();
  const blankF = { q: "", actor: "", action: "", date_from: "", date_to: "", project_id: "", admins_only: false };
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(blankF);
  const [applied, setApplied] = useState(blankF);
  const { data: actions = [] } = useQuery({ queryKey: ["audit-actions"], queryFn: async () => (await api.get("/audit/actions")).data });
  const { data, isLoading } = useQuery({
    queryKey: ["audit", page, applied],
    queryFn: async () => (await api.get("/audit", { params: {
      page, page_size: 25,
      q: applied.q || undefined, actor: applied.actor || undefined, action: applied.action || undefined,
      date_from: applied.date_from || undefined, date_to: applied.date_to || undefined,
      project_id: applied.project_id || undefined, admins_only: applied.admins_only || undefined,
    } })).data,
  });
  const apply = () => { setPage(1); setApplied(filters); };
  const reset = () => { setFilters(blankF); setApplied(blankF); setPage(1); };
  const projName = (id) => projects.find((p) => p.id === id)?.name || "-";
  return (
    <div className="space-y-4">
    {isSuperadmin && <AdminDigestCard />}
    <SectionCard title="Audit Aktivitas" description="Pelaku, waktu, proyek, objek, dan perubahan. KTP, NIK, dan kata sandi tidak dicatat." className="!p-0 overflow-hidden">
      <div className="border-b border-border p-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-6">
          <Select value={filters.action || "all"} onValueChange={(v) => setFilters({ ...filters, action: v === "all" ? "" : v })}>
            <SelectTrigger data-testid="audit-action"><SelectValue placeholder="Semua aksi" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Semua aksi</SelectItem>
              {actions.map((a) => <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="Cari alasan/objek" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} data-testid="audit-search" />
          <Input placeholder="Pelaku (email)" value={filters.actor} onChange={(e) => setFilters({ ...filters, actor: e.target.value })} data-testid="audit-actor" />
          <Input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} data-testid="audit-from" />
          <Input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} data-testid="audit-to" />
          <div className="flex gap-2">
            <Button onClick={apply} className="flex-1" data-testid="audit-apply">Terapkan</Button>
            <Button variant="outline" onClick={reset} data-testid="audit-reset">Reset</Button>
          </div>
        </div>
        {isSuperadmin && (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <Select value={filters.project_id || "all"} onValueChange={(v) => setFilters({ ...filters, project_id: v === "all" ? "" : v })}>
              <SelectTrigger className="w-56" data-testid="audit-project"><SelectValue placeholder="Semua proyek" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua proyek</SelectItem>
                {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 text-sm">
              <Switch checked={filters.admins_only} onCheckedChange={(v) => setFilters({ ...filters, admins_only: v })} data-testid="audit-admins-only" />
              Hanya aktivitas admin
            </label>
          </div>
        )}
      </div>
      {isLoading ? <div className="p-6"><Loading /></div> : !data?.items?.length ? (
        <div className="p-6"><EmptyState title="Tidak ada log" description="Tidak ada aktivitas yang cocok dengan filter." icon={ScrollText} /></div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>Waktu</TableHead><TableHead>Pelaku</TableHead><TableHead>Proyek</TableHead><TableHead>Aksi</TableHead><TableHead>Objek</TableHead><TableHead>Alasan</TableHead></TableRow></TableHeader>
              <TableBody>
                {data.items.map((a) => (
                  <TableRow key={a.id} data-testid={`audit-row-${a.id}`}>
                    <TableCell className="whitespace-nowrap text-xs">{formatTanggalWaktu(a.created_at)}</TableCell>
                    <TableCell className="text-xs">{a.actor_email || "-"}</TableCell>
                    <TableCell className="text-xs">{a.project_id ? projName(a.project_id) : "-"}</TableCell>
                    <TableCell className="text-xs font-medium">{ACTION_LABELS[a.action] || a.action}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{OBJECT_LABELS[a.object_type] || a.object_type}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{a.reason || "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="p-4"><Pagination page={page} total={data.total} pageSize={data.page_size} onPage={setPage} /></div>
        </>
      )}
    </SectionCard>
    </div>
  );
}

function yesterdayWib() {
  const d = new Date(Date.now() + 7 * 3600 * 1000 - 24 * 3600 * 1000);
  return d.toISOString().slice(0, 10);
}

function AdminDigestCard() {
  const [date, setDate] = useState(yesterdayWib());
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);
  const openPreview = async () => {
    try { setPreview((await api.get("/audit/admin-digest/preview", { params: { date } })).data); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const send = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/audit/admin-digest/send", null, { params: { date } });
      toast.success(`Ringkasan aktivitas admin terkirim (${data.count} tindakan).`);
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  };
  return (
    <SectionCard title="Email Harian Aktivitas Admin" description="Dikirim otomatis setiap hari pukul 07.00 WIB ke email superadmin, berisi semua tindakan admin proyek pada hari sebelumnya (tidak dikirim bila tidak ada aktivitas)." testid="admin-digest-card">
      <div className="flex flex-wrap items-end gap-2">
        <div><Label className="text-xs">Tanggal aktivitas</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-44" data-testid="admin-digest-date" /></div>
        <Button variant="outline" onClick={openPreview} data-testid="admin-digest-preview"><Eye className="mr-2 h-4 w-4" /> Pratinjau</Button>
        <Button onClick={send} disabled={sending} data-testid="admin-digest-send"><Mail className="mr-2 h-4 w-4" /> Kirim Sekarang</Button>
      </div>
      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-3xl" data-testid="admin-digest-preview-dialog">
          <DialogHeader><DialogTitle>Pratinjau Email Aktivitas Admin</DialogTitle></DialogHeader>
          {preview && <div className="text-sm"><span className="text-muted-foreground">Subjek: </span><span className="font-medium" data-testid="admin-digest-subject">{preview.subject}</span></div>}
          <div className="max-h-[60vh] overflow-auto rounded-lg border border-border">
            {preview?.html && <iframe title="admin-digest" srcDoc={preview.html} sandbox="" className="h-[420px] w-full bg-white" data-testid="admin-digest-iframe" />}
          </div>
        </DialogContent>
      </Dialog>
    </SectionCard>
  );
}
